import { AUTOMATION_SOURCE, isUnknownSentinel } from './constants';

// The gate's `ocrVerification`, as the BE sends it (OcrVerificationMode).
export const OCR_VERIFICATION_MODE = {
    OFF: 'OFF',
    BOL_TRAILER: 'BOL_TRAILER',
    BOL_TRAILER_SEAL: 'BOL_TRAILER_SEAL',
};

// What one extraction reads. Also the BE's OcrIdentifier.
export const OCR_IDENTIFIER = {
    BOL_TRAILER: 'BOL_TRAILER',
    BOL_SEAL: 'BOL_SEAL',
    TRAILER: 'TRAILER',
    SEAL: 'SEAL',
};

// The two verified numbers, each with the form field it belongs to and the identifier that reads it from the BOL
// document or from the vehicle.
export const OCR_TARGET = {
    TRAILER: 'TRAILER',
    SEAL: 'SEAL',
};

export const OCR_TARGET_CONFIG = {
    [OCR_TARGET.TRAILER]: {
        dataField: 'trailer.trailerNumber',
        bolIdentifier: OCR_IDENTIFIER.BOL_TRAILER,
        vehicleIdentifier: OCR_IDENTIFIER.TRAILER,
        label: 'Trailer #',
    },
    [OCR_TARGET.SEAL]: {
        dataField: 'cargo.sealNumber',
        bolIdentifier: OCR_IDENTIFIER.BOL_SEAL,
        vehicleIdentifier: OCR_IDENTIFIER.SEAL,
        label: 'Seal #',
    },
};

export const OCR_IDENTIFIER_LABEL = {
    [OCR_IDENTIFIER.BOL_TRAILER]: 'BOL Trailer #',
    [OCR_IDENTIFIER.BOL_SEAL]: 'BOL Seal #',
    [OCR_IDENTIFIER.TRAILER]: 'Trailer #',
    [OCR_IDENTIFIER.SEAL]: 'Seal #',
};

// Also the BE's OcrExtractionStatus; PENDING exists only in the browser.
export const OCR_EXTRACTION_STATUS = {
    PENDING: 'PENDING',
    EXTRACTED: 'EXTRACTED',
    UNREADABLE: 'UNREADABLE',
    FAILED: 'FAILED',
};

// Also the BE's OcrMatchResult and OcrValueSource.
export const OCR_MATCH_RESULT = {
    MATCH: 'MATCH',
    MISMATCH: 'MISMATCH',
    INCOMPLETE: 'INCOMPLETE',
};

export const OCR_VALUE_SOURCE = {
    MACAW: 'MACAW',
    CONDOR: 'CONDOR',
};

// Why a verified number blocks submit. Also the `kind` in the OCR_VERIFICATION override snapshot.
export const OCR_ISSUE = {
    IN_PROGRESS: 'IN_PROGRESS',
    NOT_COMPLETED: 'NOT_COMPLETED',
    INCOMPLETE: 'INCOMPLETE',
    MISMATCH: 'MISMATCH',
    FIELD_DISCREPANCY: 'FIELD_DISCREPANCY',
};

// Camera types that offer "Get text from photo". A BOL camera reads the BOL document, the others the vehicle.
export const OCR_CAMERA_TYPES = ['BOL', 'TRAILER', 'PTZ'];
const BOL_CAMERA_TYPE = 'BOL';

const KNOWN_MODES = new Set(Object.values(OCR_VERIFICATION_MODE));

// An unknown mode (a newer BE) is treated as OFF, so it never blocks a submit the agent cannot satisfy.
export const resolveOcrVerificationMode = (gate) => (KNOWN_MODES.has(gate?.ocrVerification) ? gate.ocrVerification : OCR_VERIFICATION_MODE.OFF);

export const isOcrVerificationEnabled = (mode) => !!mode && mode !== OCR_VERIFICATION_MODE.OFF;

export const getOcrTargets = (mode) => {
    if (mode === OCR_VERIFICATION_MODE.BOL_TRAILER) return [OCR_TARGET.TRAILER];
    if (mode === OCR_VERIFICATION_MODE.BOL_TRAILER_SEAL) return [OCR_TARGET.TRAILER, OCR_TARGET.SEAL];
    return [];
};

// The menu of the camera's "Get text from photo" action; the first entry is the default a plain click runs.
export function getOcrMenuOptions(cameraType, mode) {
    if (!OCR_CAMERA_TYPES.includes(cameraType)) return [];
    const identifierKey = cameraType === BOL_CAMERA_TYPE ? 'bolIdentifier' : 'vehicleIdentifier';
    return getOcrTargets(mode).map((target) => OCR_TARGET_CONFIG[target][identifierKey]);
}

// Byte-for-byte the BE's TruckTransactionOcrVerificationService.normalize: Java's `\s` is ASCII whitespace only,
// so JS's wider `\s` would let a value with a non-breaking space match here and be rejected there.
const JAVA_WHITESPACE = /[ \t\n\v\f\r]+/g;

export const normalizeOcrValue = (value) => (value == null ? '' : String(value).replace(JAVA_WHITESPACE, '').toUpperCase());

export function computeOcrMatchResult(bolValue, vehicleValue) {
    const bol = normalizeOcrValue(bolValue);
    const vehicle = normalizeOcrValue(vehicleValue);
    if (!bol || !vehicle) return OCR_MATCH_RESULT.INCOMPLETE;
    return bol === vehicle ? OCR_MATCH_RESULT.MATCH : OCR_MATCH_RESULT.MISMATCH;
}

export const isLoadedCargoStatus = (cargoStatus) => cargoStatus != null && String(cargoStatus).trim().toUpperCase() === 'LOADED';

// Mandatory only at submit, and only while the cargo is Loaded.
export const isOcrVerificationRequired = (mode, cargoStatus) => isOcrVerificationEnabled(mode) && isLoadedCargoStatus(cargoStatus);

const isCompleted = (attempt) => attempt.status !== OCR_EXTRACTION_STATUS.PENDING;

const hasOcrValue = (attempt) => attempt?.status === OCR_EXTRACTION_STATUS.EXTRACTED && !!normalizeOcrValue(attempt.value);

// Attempts are kept in request order and one identifier never has two in flight, so its latest completed attempt is
// its current result, a failed one included: a failed retry does not fall back to an older value.
export const getCurrentOcrAttempt = (attempts, identifier) => {
    for (let i = (attempts?.length ?? 0) - 1; i >= 0; i--) {
        const attempt = attempts[i];
        if (attempt.identifier === identifier && isCompleted(attempt)) return attempt;
    }
    return null;
};

export const isOcrIdentifierInFlight = (attempts, identifier) =>
    (attempts || []).some((attempt) => attempt.identifier === identifier && attempt.status === OCR_EXTRACTION_STATUS.PENDING);

// Macaw (ALISA) values of a field, newest first: the suggestion history plus the current automation value, which the
// history can miss (it is not recorded once the agent edited the field). Unknown sentinels are not values.
export function getMacawOcrValues(aiSuggestions, automation) {
    const values = [];
    const seen = new Set();
    const add = (value, timestamp) => {
        const normalized = normalizeOcrValue(value);
        if (!normalized || isUnknownSentinel(value) || seen.has(normalized)) return;
        seen.add(normalized);
        values.push({ value: String(value), timestamp: timestamp ?? null });
    };
    (aiSuggestions || []).forEach(({ value, timestamp }) => add(value, timestamp));
    if (automation?.source === AUTOMATION_SOURCE.AI_OCR) add(automation.originalValue ?? automation.sourceValue, null);
    return values;
}

/**
 * The verification of one number (trailer or seal) from the current values.
 *
 * Trailer: the BOL value matches when any vehicle value (the current Condor read or a Macaw value) is equal. Seal:
 * only the current Condor read counts, as on the BE. The "compared" vehicle value is the matching one, else the newest
 * one; it is the value shown in colour and sent to the BE, which recomputes the result from exactly that pair.
 */
export function resolveOcrTargetVerification({ target, attempts, macawValues = [] }) {
    const { bolIdentifier, vehicleIdentifier } = OCR_TARGET_CONFIG[target];
    const bolAttempt = getCurrentOcrAttempt(attempts, bolIdentifier);
    const condorAttempt = getCurrentOcrAttempt(attempts, vehicleIdentifier);
    const bolValue = hasOcrValue(bolAttempt) ? bolAttempt.value : null;

    const candidates = [];
    if (hasOcrValue(condorAttempt)) {
        candidates.push({ value: condorAttempt.value, source: OCR_VALUE_SOURCE.CONDOR, timestamp: condorAttempt.completedAt ?? null });
    }
    if (target === OCR_TARGET.TRAILER) {
        macawValues.forEach(({ value, timestamp }) => candidates.push({ value, source: OCR_VALUE_SOURCE.MACAW, timestamp }));
    }
    candidates.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));

    const normalizedBol = normalizeOcrValue(bolValue);
    const matched = normalizedBol ? candidates.find((candidate) => normalizeOcrValue(candidate.value) === normalizedBol) : undefined;
    const compared = matched ?? candidates[0] ?? null;

    return {
        target,
        bolAttempt,
        condorAttempt,
        bolValue,
        compared,
        result: computeOcrMatchResult(bolValue, compared?.value),
        newestMacawValue: macawValues[0]?.value ?? null,
        inFlight: isOcrIdentifierInFlight(attempts, bolIdentifier) || isOcrIdentifierInFlight(attempts, vehicleIdentifier),
        hasAttempts: (attempts || []).some(
            (attempt) => isCompleted(attempt) && (attempt.identifier === bolIdentifier || attempt.identifier === vehicleIdentifier)
        ),
    };
}

// A field holding only the "/" unknown filler (quick submit puts it in empty required fields) counts as empty.
export const isEmptyOcrFieldValue = (value) => !normalizeOcrValue(value) || isUnknownSentinel(value) || String(value).trim() === '/';

// Product decision (2026-10-22): a value ALISA filled in is replaced by the verified number; what the agent typed,
// or what came from another source (appointment, inventory, history), is never overwritten and blocks instead.
// A Condor value is one OCR set itself on an earlier submit (the dropdown records a pick as the agent's): a newer
// verified number may replace it like an ALISA value.
const MACHINE_FILLED_SOURCES = new Set([AUTOMATION_SOURCE.AI_OCR, AUTOMATION_SOURCE.CONDOR]);

const isReplaceableOcrFieldValue = (value, source) => isEmptyOcrFieldValue(value) || MACHINE_FILLED_SOURCES.has(source);

const differsFromVerified = (value, verification) => normalizeOcrValue(value) !== normalizeOcrValue(verification.compared.value);

// Fields to set on submit: only with a value that verified, only where the field is empty or holds an ALISA or an
// earlier OCR value.
export function getOcrAutofillValues(verifications, fieldValues, fieldValueSources) {
    const autofill = {};
    (verifications || []).forEach((verification) => {
        if (verification.result !== OCR_MATCH_RESULT.MATCH) return;
        const { dataField } = OCR_TARGET_CONFIG[verification.target];
        const value = fieldValues?.[dataField];
        if (isReplaceableOcrFieldValue(value, fieldValueSources?.[dataField]) && differsFromVerified(value, verification)) {
            autofill[dataField] = { value: verification.compared.value, source: verification.compared.source };
        }
    });
    return autofill;
}

function getTargetIssue(verification, fieldValue, fieldSource) {
    if (verification.inFlight) return OCR_ISSUE.IN_PROGRESS;
    if (verification.result === OCR_MATCH_RESULT.MISMATCH) return OCR_ISSUE.MISMATCH;
    if (verification.result === OCR_MATCH_RESULT.INCOMPLETE) return verification.hasAttempts ? OCR_ISSUE.INCOMPLETE : OCR_ISSUE.NOT_COMPLETED;
    // The BE requires the submitted number to equal the verified vehicle value. A replaceable value is not an
    // issue: submit sets the verified number first (getOcrAutofillValues).
    if (!isReplaceableOcrFieldValue(fieldValue, fieldSource) && differsFromVerified(fieldValue, verification)) return OCR_ISSUE.FIELD_DISCREPANCY;
    return null;
}

// What blocks submit, one entry per verified number that is not clean. Empty when nothing blocks.
export function getOcrSubmitIssues({ required, verifications, fieldValues, fieldValueSources }) {
    if (!required) return [];
    return (verifications || [])
        .map((verification) => {
            const { dataField } = OCR_TARGET_CONFIG[verification.target];
            const fieldValue = fieldValues?.[dataField];
            const kind = getTargetIssue(verification, fieldValue, fieldValueSources?.[dataField]);
            return kind ? { target: verification.target, kind, fieldValue: fieldValue ?? null } : null;
        })
        .filter(Boolean);
}

// The `ocrVerification` of the submit body (BE OcrVerificationDto): one final value per field.
export function buildOcrVerificationPayload(verifications) {
    const byTarget = Object.fromEntries((verifications || []).map((verification) => [verification.target, verification]));
    const trailer = byTarget[OCR_TARGET.TRAILER];
    const seal = byTarget[OCR_TARGET.SEAL];
    const payload = {};

    if (trailer) {
        const comparedMacaw = trailer.compared?.source === OCR_VALUE_SOURCE.MACAW ? trailer.compared.value : null;
        payload.bolTrailer = trailer.bolValue;
        payload.macawTrailer = comparedMacaw ?? trailer.newestMacawValue;
        payload.condorTrailer = hasOcrValue(trailer.condorAttempt) ? trailer.condorAttempt.value : null;
        payload.sourceTrailer = trailer.compared?.source ?? null;
        payload.resultTrailer = trailer.result;
    }
    if (seal) {
        payload.bolSeal = seal.bolValue;
        payload.condorSeal = seal.compared?.value ?? null;
        payload.resultSeal = seal.result;
    }
    return payload;
}

// The `resultSnapshot` of the OCR_VERIFICATION override. The BE requires it non-empty; values only, never images.
export const buildOcrVerificationSnapshot = ({ mode, issues, verifications }) => ({
    mode,
    issues: (issues || []).map(({ target, kind }) => ({ target, kind })),
    verification: buildOcrVerificationPayload(verifications),
});

const newestFirst = (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0);

/**
 * What a verified field shows: the BOL = / != vehicle chip, and its dropdown rows grouped as "BOL document" and
 * "Numbers on vehicle", newest first. Only the compared rows carry a tone. `null` until a read for this field
 * completed, so a field keeps its usual look before the agent used OCR on it.
 */
export function buildOcrFieldView({ verification, attempts, macawValues = [] }) {
    if (!verification?.hasAttempts) return null;
    const { bolIdentifier, vehicleIdentifier } = OCR_TARGET_CONFIG[verification.target];
    const tone = verification.result === OCR_MATCH_RESULT.MATCH ? 'match' : 'mismatch';
    const { compared, bolAttempt, condorAttempt } = verification;
    const completed = (attempts || []).filter(isCompleted);

    const attemptRow = (attempt, isCompared) => ({
        key: attempt.id,
        value: hasOcrValue(attempt) ? attempt.value : null,
        source: OCR_VALUE_SOURCE.CONDOR,
        cameraName: attempt.cameraName,
        timestamp: attempt.completedAt,
        tone: isCompared ? tone : null,
    });

    const bolRows = completed
        .filter((attempt) => attempt.identifier === bolIdentifier)
        .map((attempt) => attemptRow(attempt, attempt === bolAttempt))
        .sort(newestFirst);

    // With no readable vehicle value the current Condor read is the compared side, shown as unknown.
    const condorCompared = compared ? compared.source === OCR_VALUE_SOURCE.CONDOR : true;
    const comparedMacaw = compared?.source === OCR_VALUE_SOURCE.MACAW ? normalizeOcrValue(compared.value) : null;
    const vehicleRows = [
        ...completed
            .filter((attempt) => attempt.identifier === vehicleIdentifier)
            .map((attempt) => attemptRow(attempt, condorCompared && attempt === condorAttempt)),
        ...macawValues.map(({ value, timestamp }) => ({
            key: `macaw-${normalizeOcrValue(value)}`,
            value,
            source: OCR_VALUE_SOURCE.MACAW,
            cameraName: null,
            timestamp,
            tone: comparedMacaw !== null && normalizeOcrValue(value) === comparedMacaw ? tone : null,
        })),
    ].sort(newestFirst);

    let selectableIndex = 0;
    const withSelection = (rows) => rows.map((row) => ({ ...row, selectableIndex: row.value === null ? -1 : selectableIndex++ }));
    const groups = [
        { key: 'BOL', rows: withSelection(bolRows) },
        { key: 'VEHICLE', rows: withSelection(vehicleRows) },
    ].filter(({ rows }) => rows.length > 0);

    return {
        target: verification.target,
        result: verification.result,
        chip: { bolValue: verification.bolValue, vehicleValue: compared?.value ?? null, tone },
        groups,
        selectable: groups.flatMap(({ rows }) => rows.filter((row) => row.value !== null).map(({ value }) => ({ value }))),
    };
}

const pad = (n) => String(n).padStart(2, '0');

// 24-hour HH:mm of a value's read time, as the field details show it.
export const formatOcrValueTime = (timestamp) => {
    if (!Number.isFinite(timestamp)) return '';
    const date = new Date(timestamp);
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
